<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Sidebar (Handles its own mobile overlay logic) -->
    <HrSidebar v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <!-- md:ml-64 pushes this entire section to the right of the sidebar on desktop. -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar (Sticky, stays at top of this wrapper) -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- Page Header & Search -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Staff Directory</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage and view all active staff members</p>
          </div>
          <div class="relative w-full sm:w-80">
            <input 
              v-model="searchQuery"
              type="text" 
              placeholder="Search by name, position, or ID..."
              class="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#035a76] focus:border-transparent transition-shadow"
            >
            <div class="absolute left-3 top-3 text-gray-400 dark:text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Section Tabs -->
        <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav class="-mb-px flex space-x-6 overflow-x-auto pb-1 scrollbar-hide">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                activeTab === tab.id 
                  ? 'border-[#76ba51] text-[#76ba51] dark:text-[#76ba51]' 
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600', 
                'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors'
              ]"
            >
              {{ tab.name }}
            </button>
          </nav>
        </div>

        <!-- Staff Table -->
        <div class="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Staff ID</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Position</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Joining Date</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th scope="col" class="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                <!-- Use filteredStaff here instead of staffMembers -->
                <tr v-for="staff in filteredStaff" :key="staff.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{{ staff.id }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <div class="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                        <span class="text-white text-sm font-semibold">{{ getInitials(staff.full_name || staff.applicant_name) }}</span>
                      </div>
                      <div class="ml-4">
                        <div class="text-sm font-medium text-gray-900 dark:text-white">{{ staff.full_name || staff.applicant_name }}</div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">{{ staff.email || staff.applicant_email }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ staff.position_title }}</div>
                    <div v-if="staff.section_name" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {{ staff.section_name }} <span v-if="staff.class_name">• {{ staff.class_name }}</span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ formatDate(staff.joining_date) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="getStatusClass(staff.status)" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold">
                      <span class="w-1.5 h-1.5 rounded-full mr-1.5" :class="getStatusDot(staff.status)"></span>
                      {{ staff.status || 'N/A' }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div class="flex items-center justify-end space-x-3">
                      <button @click="viewStaff(staff.id)" class="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" title="View Details">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button @click="editStaff(staff.id)" class="text-gray-600 dark:text-gray-400 hover:text-amber-500 dark:hover:text-amber-400 transition-colors" title="Edit Staff">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
                
                <!-- Empty State -->
                <tr v-if="filteredStaff.length === 0">
                  <td colspan="6" class="px-6 py-12 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No staff found</h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Try adjusting your search or filter criteria.</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Staff Details Modal -->
        <StaffDetailsModal 
          v-if="selectedStaff"
          :staff="selectedStaff"
          @close="selectedStaff = null"
        />

      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/services/api';
import HrSidebar from '../../components/hr/HrSidebar.vue';
import HrTopbar from '../../components/hr/HrTopbar.vue';
import StaffDetailsModal from '../../components/hr/StaffDetailsModal.vue';

const router = useRouter();

const isSidebarOpen = ref(false); // Hidden by default on mobile
const isDarkMode = ref(false);
const currentPageTitle = ref('Staff Directory');

const staffMembers = ref([]);
const activeTab = ref('all');
const searchQuery = ref('');
const selectedStaff = ref(null);

const tabs = [
  { id: 'all', name: 'All Staff' },
  { id: 'nursery', name: 'Nursery' },
  { id: 'primary', name: 'Primary' },
  { id: 'secondary', name: 'Secondary' },
  { id: 'outsource', name: 'Outsourced' },
  { id: 'non-teaching', name: 'Non-Teaching' },
  { id: 'extracurricular', name: 'Extracurricular' }
];

const fetchStaffMembers = async () => {
  try {
    const response = await api.get('get_hired_applicants.php');
    staffMembers.value = response.data.data.map(item => ({
      id: item.applicant_id,
      applicant_name: item.applicant_name,
      applicant_email: item.applicant_email,
      full_name: item.full_name,
      email: item.email,
      position_title: item.position?.position_title,
      section_name: item.section?.section_name,
      class_name: item.class?.class_name,
      joining_date: item.contract?.joining_date,
      status: item.applicant_status
    }));
  } catch (error) {
    console.error('Error fetching staff members:', error);
  }
};

// ✅ FIXED: Computed property now correctly references mapped properties
const filteredStaff = computed(() => {
  let filtered = staffMembers.value;
  
  // Filter by tab
  if (activeTab.value !== 'all') {
    filtered = filtered.filter(staff => {
      const section = (staff.section_name || '').toLowerCase();
      const position = (staff.position_title || '').toLowerCase();
      
      if (activeTab.value === 'nursery') return section.includes('nursery');
      if (activeTab.value === 'primary') return section.includes('primary');
      if (activeTab.value === 'secondary') return section.includes('secondary');
      if (activeTab.value === 'outsource') return position.includes('outsource') || position.includes('security') || position.includes('cleaning');
      if (activeTab.value === 'non-teaching') return position.includes('admin') || position.includes('non-teaching') || position.includes('support');
      if (activeTab.value === 'extracurricular') return position.includes('extra') || position.includes('sports') || position.includes('arts');
      return true;
    });
  }
  
  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(staff => {
      const name = (staff.full_name || staff.applicant_name || '').toLowerCase();
      const position = (staff.position_title || '').toLowerCase();
      const email = (staff.email || staff.applicant_email || '').toLowerCase();
      const id = String(staff.id || '').toLowerCase();
      
      return name.includes(query) ||
             position.includes(query) ||
             email.includes(query) ||
             id.includes(query);
    });
  }
  
  return filtered;
});

const viewStaff = (id) => {
  router.push(`/staff/${id}`);
};

const editStaff = (id) => {
  console.log('Edit staff:', id);
  // TODO: Implement edit logic
};

const getInitials = (name) => {
  if (!name) return '??';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

const getStatusClass = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('active') || s.includes('hired')) return 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  if (s.includes('leave')) return 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
  if (s.includes('inactive') || s.includes('terminated')) return 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  return 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
};

const getStatusDot = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('active') || s.includes('hired')) return 'bg-green-500';
  if (s.includes('leave')) return 'bg-yellow-500';
  if (s.includes('inactive') || s.includes('terminated')) return 'bg-red-500';
  return 'bg-gray-500';
};

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
};

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark');
    localStorage.theme = 'dark';
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.theme = 'light';
  }
};

onMounted(() => {
  fetchStaffMembers();
});
</script>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
/* Hide scrollbar for IE, Edge and Firefox */
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
</style>